import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Sparkles,
  Palette,
  Eye,
  TrendingUp,
  MapPin,
  Award,
  Target,
} from "lucide-react";
import QuoteDialog from "@/components/site/QuoteDialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const TITLE = "Création de logo à La Réunion — EM Group | Agence premium";
const DESCRIPTION =
  "Création de logo à La Réunion par EM Group : identité visuelle sur-mesure, branding complet, accompagnement stratégique. Transformez votre image pour attirer plus de clients. Devis sous 24h.";
const URL =
  typeof window !== "undefined"
    ? `${window.location.origin}/creation-logo-la-reunion`
    : "";

const FAQ_ITEMS = [
  {
    q: "Combien coûte une création de logo à La Réunion ?",
    a: "Chez EM Group, une création de logo à La Réunion démarre à 699 € TTC avec le Pack Logo Essentiel (logo, déclinaisons, fichiers HD et 2 visuels offerts). Pour un branding complet (palette, typographies, charte graphique, univers visuel), comptez à partir de 1599 € TTC. Le tarif final dépend de votre secteur et de l'ampleur du projet. Vous recevez toujours un devis détaillé avant commande.",
  },
  {
    q: "Pourquoi faire appel à une agence pour créer son logo ?",
    a: "Un logo professionnel n'est pas qu'un dessin : c'est un outil stratégique qui influence directement la perception et la décision d'achat de vos clients. Faire appel à une agence comme EM Group, c'est s'assurer d'une cohérence visuelle complète, d'un logo vectoriel de qualité (utilisable partout), et d'un accompagnement stratégique — pas d'un simple template générique.",
  },
  {
    q: "Combien de temps faut-il pour créer un logo professionnel ?",
    a: "Comptez généralement 5 à 10 jours pour un Pack Logo Essentiel et 2 à 3 semaines pour un Pack Branding Complet. Ce délai inclut un brief approfondi, plusieurs propositions de concepts, les révisions et la livraison des fichiers finaux. Nous pouvons accélérer la production en cas d'urgence — précisez-le dès le brief.",
  },
  {
    q: "Est-il possible de faire évoluer son logo plus tard ?",
    a: "Oui, tout à fait. Nous vous livrons toujours les fichiers sources (vectoriels éditables), ce qui permet de faire évoluer votre logo dans le temps — refresh, déclinaisons, nouvelles versions. Vous pouvez également passer du Pack Logo au Pack Branding Complet à tout moment, ou basculer sur un accompagnement complet pour aller plus loin.",
  },
  {
    q: "Travaillez-vous uniquement à La Réunion ou aussi à distance ?",
    a: "Nous intervenons partout à La Réunion (Saint-Denis, Saint-Pierre, Saint-Paul, Le Tampon, Saint-Gilles…) et travaillons aussi à distance, par visio, téléphone et WhatsApp. Nous accompagnons également des clients à Maurice et en France métropolitaine. Le processus reste fluide et identique : brief, propositions, révisions et livraison.",
  },
];

function setMeta(name, content, attr = "name") {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function CreationLogoReunion() {
  const [quoteOpen, setQuoteOpen] = useState(false);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = TITLE;
    setMeta("description", DESCRIPTION);
    setMeta("keywords", "création de logo La Réunion, logo 974, branding Réunion, identité visuelle Réunion, agence digitale La Réunion, graphiste Réunion");
    setMeta("og:title", TITLE, "property");
    setMeta("og:description", DESCRIPTION, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:locale", "fr_RE", "property");
    if (URL) setMeta("og:url", URL, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", TITLE);
    setMeta("twitter:description", DESCRIPTION);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    if (URL) canonical.setAttribute("href", URL);

    // JSON-LD structured data
    let ld = document.getElementById("ld-logo-reunion");
    if (!ld) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.id = "ld-logo-reunion";
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Création de logo et identité visuelle",
      provider: {
        "@type": "LocalBusiness",
        name: "EM Group",
        email: "contact@emgroupdigital.com",
        telephone: "+262262667970",
        url: URL || "https://emgroupdigital.com",
        address: {
          "@type": "PostalAddress",
          streetAddress: "60 rue François 1er",
          addressLocality: "Paris",
          postalCode: "75008",
          addressCountry: "FR",
        },
        areaServed: [
          { "@type": "Place", name: "La Réunion" },
          { "@type": "Place", name: "Maurice" },
          { "@type": "Place", name: "France" },
        ],
      },
      areaServed: { "@type": "AdministrativeArea", name: "La Réunion" },
      offers: {
        "@type": "Offer",
        priceCurrency: "EUR",
        valueAddedTaxIncluded: true,
        price: "699",
        description:
          "Pack Logo Essentiel : logo + déclinaisons + 2 visuels offerts.",
      },
      description: DESCRIPTION,
    });

    // FAQ JSON-LD
    let ldFaq = document.getElementById("ld-logo-reunion-faq");
    if (!ldFaq) {
      ldFaq = document.createElement("script");
      ldFaq.type = "application/ld+json";
      ldFaq.id = "ld-logo-reunion-faq";
      document.head.appendChild(ldFaq);
    }
    ldFaq.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ_ITEMS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });

    return () => {
      document.title = prevTitle;
    };
  }, []);

  const openQuote = () => setQuoteOpen(true);

  return (
    <div className="min-h-screen bg-[#050507] text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/brand/logo-em-group.png"
              alt="EM Group"
              className="h-10 w-10 object-contain"
            />
            <span className="font-outfit tracking-tight text-lg">
              EM <span className="text-white/50">Group</span>
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft size={16} /> Accueil
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative py-24 md:py-36 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_30%,rgba(229,36,49,0.2),transparent_55%),radial-gradient(ellipse_at_70%_70%,rgba(31,44,110,0.25),transparent_55%)]" />
        <div className="grain absolute inset-0" />

        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-8">
            <MapPin size={14} className="text-[#E52431]" />
            <span className="text-xs uppercase tracking-[0.2em] text-white/75">
              La Réunion · 974
            </span>
          </div>

          <h1 className="font-outfit text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[1.05]">
            Création de logo{" "}
            <span className="gradient-text font-medium">à La Réunion</span>
            <br className="hidden md:block" />
            <span className="text-white/85">pour entreprises ambitieuses</span>
          </h1>

          <p className="mt-8 text-white/75 text-lg md:text-xl font-light leading-relaxed max-w-3xl">
            <strong className="text-white font-normal">
              Vous lancez votre entreprise ou souhaitez améliorer votre image ?
            </strong>{" "}
            Un logo professionnel est la base pour inspirer confiance et
            attirer vos premiers clients.
          </p>

          <p className="mt-5 text-white/65 text-base md:text-lg font-light leading-relaxed max-w-3xl">
            EM Group conçoit des logos sur-mesure pour les entrepreneurs, PME
            et marques basés à <strong className="text-white font-normal">
              La Réunion</strong>, pensés pour incarner votre identité et{" "}
            <strong className="text-white font-normal">
              convertir vos prospects en clients
            </strong>
            .
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <button
              onClick={openQuote}
              className="inline-flex items-center gap-3 rounded-full gradient-bg text-white font-semibold px-10 py-5 text-sm uppercase tracking-[0.15em] hover:shadow-[0_0_50px_rgba(229,36,49,0.5)] hover:scale-[1.02] transition-all duration-300 btn-shine"
            >
              Créer mon logo dès maintenant
              <ArrowRight size={16} />
            </button>
            <a
              href="#packs"
              className="inline-flex items-center gap-2 rounded-full glass text-white font-medium px-8 py-5 text-sm uppercase tracking-[0.15em] hover:bg-white/10 transition-all duration-300"
            >
              Voir les tarifs
            </a>
          </div>
          <p className="mt-5 text-xs text-white/50 font-light">
            ⚡ Réponse sous 24h · Devis personnalisé · Sans engagement
          </p>
        </div>
      </section>

      {/* Service presentation */}
      <section className="py-20 md:py-28 section-light">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
            — Le service
          </span>
          <h2 className="mt-4 font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#0A0A0A]">
            Un logo qui raconte votre histoire
          </h2>
          <div className="mt-8 space-y-5 text-[#0A0A0A]/70 text-lg font-light leading-relaxed">
            <p>
              Un logo n&rsquo;est pas qu&rsquo;un dessin. C&rsquo;est la{" "}
              <span className="text-[#0A0A0A] font-normal">
                porte d&rsquo;entrée de votre marque
              </span>{" "}
              : ce qui reste en mémoire, ce qui inspire confiance et ce qui
              distingue votre entreprise de la concurrence.
            </p>
            <p>
              Chez EM Group, nous concevons chaque{" "}
              <strong className="text-[#0A0A0A] font-normal">
                création de logo à La Réunion
              </strong>{" "}
              comme un véritable projet stratégique. Nous étudions votre
              activité, votre cible, vos valeurs et votre positionnement pour
              produire un logo cohérent, impactant et durable.
            </p>
            <p>
              Chez EM Group, la{" "}
              <strong className="text-[#0A0A0A] font-normal">
                création de logo ne se limite pas à un design
              </strong>
              . Nous vous accompagnons de l&rsquo;image de marque jusqu&rsquo;à
              vos premiers clients, avec une vision 360° et une exécution
              pensée pour la performance.
            </p>
            <p>
              Qu&rsquo;il s&rsquo;agisse d&rsquo;un restaurant à Saint-Denis,
              d&rsquo;une boutique à Saint-Pierre, d&rsquo;un service à
              Saint-Gilles ou d&rsquo;un e-commerce réunionnais, nous adaptons
              notre approche à votre réalité locale et à vos ambitions.
            </p>
            <p>
              <a
                href="#accompagnement"
                className="inline-flex items-center gap-2 text-[#E52431] font-medium hover:underline"
              >
                Découvrir notre accompagnement complet →
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Branding importance */}
      <section className="py-20 md:py-28 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
                — L&rsquo;importance du branding
              </span>
              <h2 className="mt-4 font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white">
                Sans branding, pas de{" "}
                <span className="gradient-text font-medium">croissance</span>.
              </h2>
              <div className="mt-8 space-y-5 text-white/70 text-lg font-light leading-relaxed">
                <p>
                  À La Réunion comme ailleurs, les consommateurs choisissent
                  d&rsquo;abord une marque avant de choisir un produit. Selon
                  de nombreuses études, plus de{" "}
                  <strong className="text-white font-normal">
                    60% des décisions d&rsquo;achat
                  </strong>{" "}
                  sont influencées par la perception visuelle d&rsquo;une
                  entreprise.
                </p>
                <p>
                  Un logo professionnel, combiné à une identité visuelle
                  cohérente (couleurs, typographies, ton de marque),
                  rassure, crédibilise et{" "}
                  <strong className="text-white font-normal">
                    déclenche l&rsquo;acte d&rsquo;achat
                  </strong>
                  .
                </p>
                <p>
                  C&rsquo;est pour cela qu&rsquo;un logo seul ne suffit
                  souvent pas. Un vrai branding intègre le logo dans un
                  écosystème visuel complet, prêt à être déployé sur vos
                  supports digitaux et print.
                </p>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl bg-gradient-to-br from-[#1a0f12] to-[#0B1030] border border-white/10 p-8 space-y-5">
                {[
                  { icon: Eye, label: "Reconnaissance immédiate" },
                  { icon: Award, label: "Crédibilité professionnelle" },
                  { icon: TrendingUp, label: "Hausse du taux de conversion" },
                  { icon: Target, label: "Différenciation concurrentielle" },
                ].map((it) => {
                  const Icon = it.icon;
                  return (
                    <div key={it.label} className="flex items-center gap-4">
                      <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#E52431]/20 to-[#1F2C6E]/30 border border-white/10 grid place-items-center flex-shrink-0">
                        <Icon size={18} className="text-white" strokeWidth={1.5} />
                      </div>
                      <span className="text-white font-light">{it.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Packs */}
      <section id="packs" className="py-20 md:py-28 section-light">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
            — Nos packs logo & branding
          </span>
          <h2 className="mt-4 font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#0A0A0A]">
            Une création de logo{" "}
            <span className="gradient-text font-medium">pour chaque ambition</span>
          </h2>
          <p className="mt-6 text-[#0A0A0A]/60 text-lg font-light max-w-2xl">
            De la création du logo seul au branding complet, nous proposons
            des offres claires et transparentes pour les clients réunionnais.
          </p>

          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {[
              {
                name: "Pack Logo Essentiel",
                price: "699 € TTC",
                items: [
                  "Logo sur-mesure",
                  "Déclinaisons réseaux",
                  "Adaptation couleurs",
                  "Fichiers HD & sources",
                  "2 visuels offerts",
                ],
              },
              {
                name: "Pack Branding Complet",
                price: "À partir de 1599 € TTC",
                highlight: true,
                items: [
                  "Logo + déclinaisons",
                  "Palette couleurs",
                  "Typographies",
                  "Charte graphique",
                  "Univers visuel",
                  "Templates",
                ],
              },
              {
                name: "Pack Visibilité",
                price: "À partir de 2199 € TTC",
                items: [
                  "Branding complet",
                  "4 visuels réseaux",
                  "Mise en place réseaux",
                  "Accompagnement lancement",
                ],
              },
            ].map((p) => (
              <div
                key={p.name}
                className={`relative rounded-3xl p-8 transition-all duration-500 ${
                  p.highlight
                    ? "bg-gradient-to-b from-[#0A0A0A] to-[#141418] text-white border border-[#E52431]/40 glow-red"
                    : "light-card"
                }`}
              >
                {p.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 whitespace-nowrap">
                    <div className="inline-flex items-center gap-1.5 px-4 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] gradient-bg text-white font-semibold shadow-[0_8px_20px_rgba(229,36,49,0.35)]">
                      Le plus choisi
                    </div>
                  </div>
                )}
                <h3
                  className={`font-outfit text-xl font-medium ${
                    p.highlight ? "text-white" : "text-[#0A0A0A]"
                  }`}
                >
                  {p.name}
                </h3>
                <div
                  className={`mt-4 font-outfit text-4xl font-light ${
                    p.highlight ? "gradient-text" : "text-[#0A0A0A]"
                  }`}
                >
                  {p.price}
                </div>
                <ul className="mt-6 space-y-2.5">
                  {p.items.map((f) => (
                    <li
                      key={f}
                      className={`flex items-start gap-3 text-sm ${
                        p.highlight ? "text-white/80" : "text-[#0A0A0A]/70"
                      }`}
                    >
                      <Check
                        size={15}
                        className="text-[#E52431] mt-0.5 flex-shrink-0"
                      />
                      <span className="font-light">{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={openQuote}
                  className={`mt-7 w-full rounded-full py-3 font-semibold text-xs uppercase tracking-[0.15em] transition-all duration-300 ${
                    p.highlight
                      ? "gradient-bg text-white hover:shadow-[0_0_40px_rgba(229,36,49,0.5)]"
                      : "bg-[#0A0A0A] text-white hover:bg-[#E52431]"
                  }`}
                >
                  Demander un devis
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Accompagnement highlight */}
      <section id="accompagnement" className="py-20 md:py-28 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div
            className="relative rounded-3xl p-10 md:p-14 overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, #0A0A0A 0%, #160812 35%, #0B1030 75%, #0A0A0A 100%)",
            }}
          >
            <div className="absolute -top-32 -left-20 w-[400px] h-[400px] rounded-full bg-[#E52431]/25 blur-[100px]" />
            <div className="absolute -bottom-32 -right-20 w-[400px] h-[400px] rounded-full bg-[#1F2C6E]/30 blur-[100px]" />

            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full gradient-bg text-[10px] uppercase tracking-[0.2em] text-white font-semibold mb-6">
                <Sparkles size={11} /> L&rsquo;offre phare
              </div>

              <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white leading-[1.1]">
                Envie d&rsquo;aller{" "}
                <span className="gradient-text font-medium">plus loin</span> que
                le logo&nbsp;?
              </h2>
              <p className="mt-6 text-white/75 text-lg font-light max-w-2xl">
                L&rsquo;Accompagnement complet EM Group prend en charge chaque
                étape de votre lancement à La Réunion&nbsp;: branding, visuels,
                site web, stratégie, communication et acquisition des premiers
                clients. Tout est coordonné par une seule équipe, à votre
                service.
              </p>

              <div className="mt-8 grid sm:grid-cols-2 gap-3 max-w-2xl">
                {[
                  "Branding complet sur-mesure",
                  "Création de site web",
                  "Visuels & stratégie de communication",
                  "Lancement & acquisition",
                ].map((f) => (
                  <div
                    key={f}
                    className="flex items-center gap-3 text-white/85"
                  >
                    <Check
                      size={15}
                      className="text-[#E52431] flex-shrink-0"
                    />
                    <span className="font-light text-sm md:text-base">
                      {f}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div>
                  <div className="text-xs uppercase tracking-wider text-white/55">
                    À partir de
                  </div>
                  <div className="font-outfit text-5xl font-light text-white mt-1">
                    3490 € TTC
                  </div>
                </div>
                <button
                  onClick={openQuote}
                  className="sm:ml-auto inline-flex items-center gap-3 rounded-full gradient-bg text-white font-semibold px-8 py-4 text-sm uppercase tracking-[0.15em] hover:shadow-[0_0_50px_rgba(229,36,49,0.5)] hover:scale-[1.02] transition-all duration-300 btn-shine"
                >
                  Être accompagné
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 md:py-28 section-light">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
            — Ce que vous y gagnez
          </span>
          <h2 className="mt-4 font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#0A0A0A]">
            Les bénéfices d&rsquo;un{" "}
            <span className="gradient-text font-medium">
              logo professionnel
            </span>
          </h2>

          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {[
              {
                icon: Award,
                title: "Professionnalisme instantané",
                desc: "Un logo de qualité positionne votre entreprise au-dessus de la concurrence dès le premier regard.",
              },
              {
                icon: Palette,
                title: "Une image cohérente",
                desc: "Une identité visuelle uniforme sur vos réseaux, votre site et vos supports crée la confiance.",
              },
              {
                icon: TrendingUp,
                title: "Plus de clients, plus vite",
                desc: "Une marque forte attire, rassure et convertit mieux qu'une communication improvisée.",
              },
            ].map((b) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.title}
                  className="light-card light-card-hover rounded-3xl p-8 transition-all duration-500"
                >
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#E52431]/15 to-[#1F2C6E]/15 border border-[#E52431]/20 grid place-items-center mb-5">
                    <Icon size={20} className="text-[#E52431]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-outfit text-xl font-medium text-[#0A0A0A]">
                    {b.title}
                  </h3>
                  <p className="mt-3 text-[#0A0A0A]/65 font-light leading-relaxed">
                    {b.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Local SEO - Cities */}
      <section
        data-testid="local-cities"
        className="py-20 md:py-28 bg-[#0A0A0A] border-t border-white/5"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
              — Présence locale
            </span>
            <h2 className="mt-4 font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white">
              EM Group accompagne les{" "}
              <span className="gradient-text font-medium">
                entreprises réunionnaises
              </span>
            </h2>
            <div className="mt-6 space-y-4 text-white/70 text-lg font-light leading-relaxed">
              <p>
                Nous intervenons sur l&rsquo;ensemble de{" "}
                <strong className="text-white font-normal">La Réunion</strong>,
                aux côtés d&rsquo;entrepreneurs, commerçants, restaurateurs,
                artisans, indépendants et PME qui souhaitent structurer leur
                marque et développer leur clientèle localement.
              </p>
              <p>
                Nos projets de création de logo et de branding s&rsquo;adaptent
                aux spécificités de chaque bassin — que votre activité soit
                tournée vers le tourisme, le commerce de proximité, les
                services ou le digital.
              </p>
            </div>
          </div>

          {/* City chips */}
          <div className="mt-12 flex flex-wrap gap-3">
            {[
              "Saint-Denis",
              "Saint-Pierre",
              "Saint-Paul",
              "Saint-Gilles",
              "Le Tampon",
              "Saint-André",
              "Saint-Benoît",
              "Saint-Leu",
              "Saint-Louis",
              "La Possession",
              "Le Port",
              "Sainte-Marie",
              "Sainte-Suzanne",
              "L'Étang-Salé",
              "Bras-Panon",
            ].map((city, i) => (
              <motion.span
                key={city}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.35, delay: i * 0.03 }}
                data-testid={`city-chip-${i}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white/80 text-sm font-light hover:border-[#E52431]/40 hover:text-white hover:bg-white/10 transition-all duration-300"
              >
                <MapPin size={12} className="text-[#E52431]" />
                {city}
              </motion.span>
            ))}
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {[
              {
                title: "Commerces & restauration",
                desc: "Logos et identités visuelles pour boutiques, restaurants et snacks à Saint-Denis, Saint-Pierre ou Saint-Gilles — pensés pour se démarquer en rayon comme sur Instagram.",
              },
              {
                title: "Services & artisans",
                desc: "Identité professionnelle pour indépendants, artisans et prestataires du Tampon, de Saint-Paul ou de Saint-André souhaitant inspirer confiance dès le premier contact.",
              },
              {
                title: "E-commerce & digital",
                desc: "Branding complet pour marques réunionnaises en ligne, cabinets de conseil, start-ups du Port ou de Sainte-Marie prêtes à passer à l'échelle.",
              },
            ].map((b) => (
              <div
                key={b.title}
                className="rounded-2xl bg-gradient-to-b from-white/[0.04] to-transparent border border-white/10 p-6 hover:border-white/20 transition-all duration-500"
              >
                <h3 className="font-outfit text-lg font-medium text-white">
                  {b.title}
                </h3>
                <p className="mt-3 text-white/60 font-light text-sm leading-relaxed">
                  {b.desc}
                </p>
              </div>
            ))}
          </div>

          <p className="mt-10 text-white/55 text-sm font-light">
            Vous êtes basé à La Réunion et votre ville n&rsquo;est pas citée ?
            Nous couvrons tous les bassins et travaillons aussi à distance
            partout dans le 974 —{" "}
            <button
              onClick={openQuote}
              className="text-[#E52431] font-medium hover:underline"
            >
              parlons de votre projet
            </button>
            .
          </p>
        </div>
      </section>

      {/* Voir aussi - Internal linking */}
      <section
        data-testid="voir-aussi-section"
        className="py-20 md:py-24 section-light border-t border-black/5"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
            — Pour aller plus loin
          </span>
          <h2 className="mt-4 font-outfit text-3xl md:text-4xl font-light tracking-tight text-[#0A0A0A]">
            Explorer l&rsquo;univers{" "}
            <span className="gradient-text font-medium">EM Group</span>
          </h2>
          <p className="mt-4 text-[#0A0A0A]/65 font-light max-w-2xl">
            La création de logo n&rsquo;est qu&rsquo;une brique. Découvrez
            l&rsquo;ensemble des services pensés pour faire grandir votre
            entreprise.
          </p>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            <a
              href="/#branding"
              data-testid="voir-aussi-branding"
              className="group light-card light-card-hover rounded-3xl p-7 transition-all duration-500"
            >
              <div className="text-xs uppercase tracking-[0.2em] text-[#E52431]">
                Branding
              </div>
              <h3 className="mt-3 font-outfit text-xl font-medium text-[#0A0A0A]">
                Packs branding &amp; visuels
              </h3>
              <p className="mt-3 text-[#0A0A0A]/65 font-light text-sm leading-relaxed">
                Logos, chartes graphiques, packs réseaux sociaux et
                déclinaisons — tout l&rsquo;éventail branding EM Group.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-[#E52431] text-sm font-medium group-hover:gap-3 transition-all">
                Voir les packs <ArrowRight size={14} />
              </span>
            </a>

            <a
              href="/#sites-web"
              data-testid="voir-aussi-sites-web"
              className="group light-card light-card-hover rounded-3xl p-7 transition-all duration-500"
            >
              <div className="text-xs uppercase tracking-[0.2em] text-[#E52431]">
                Web
              </div>
              <h3 className="mt-3 font-outfit text-xl font-medium text-[#0A0A0A]">
                Création de sites web
              </h3>
              <p className="mt-3 text-[#0A0A0A]/65 font-light text-sm leading-relaxed">
                Vitrines, one-pages et e-commerce sur-mesure — pour prolonger
                votre identité visuelle sur le digital.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-[#E52431] text-sm font-medium group-hover:gap-3 transition-all">
                Découvrir <ArrowRight size={14} />
              </span>
            </a>

            <Link
              to="/creation-logo-paris"
              data-testid="voir-aussi-logo-paris"
              className="group rounded-3xl p-7 bg-gradient-to-b from-[#E52431]/[0.06] to-transparent border border-[#E52431]/25 hover:border-[#E52431]/50 transition-all duration-500"
            >
              <div className="text-xs uppercase tracking-[0.2em] text-[#E52431]">
                SEO local
              </div>
              <h3 className="mt-3 font-outfit text-xl font-medium text-[#0A0A0A]">
                Création de logo à Paris
              </h3>
              <p className="mt-3 text-[#0A0A0A]/65 font-light text-sm leading-relaxed">
                Vous êtes basé à Paris ou en Île-de-France ? Découvrez notre
                offre dédiée aux entreprises parisiennes.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-[#E52431] text-sm font-medium group-hover:gap-3 transition-all">
                Voir la page <ArrowRight size={14} />
              </span>
            </Link>

            <Link
              to="/creation-logo-ile-maurice"
              data-testid="voir-aussi-logo-maurice"
              className="group rounded-3xl p-7 bg-gradient-to-b from-[#1F2C6E]/[0.08] to-transparent border border-[#1F2C6E]/25 hover:border-[#1F2C6E]/60 transition-all duration-500"
            >
              <div className="text-xs uppercase tracking-[0.2em] text-[#E52431]">
                SEO local
              </div>
              <h3 className="mt-3 font-outfit text-xl font-medium text-[#0A0A0A]">
                Création de logo à l&rsquo;île Maurice
              </h3>
              <p className="mt-3 text-[#0A0A0A]/65 font-light text-sm leading-relaxed">
                Vous lancez un projet à Maurice ? Découvrez notre offre
                dédiée aux entreprises mauriciennes.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-[#E52431] text-sm font-medium group-hover:gap-3 transition-all">
                Voir la page <ArrowRight size={14} />
              </span>
            </Link>
          </div>

          <div className="mt-10 flex flex-wrap gap-3 text-sm">
            <a
              href="/#accompagnement"
              data-testid="voir-aussi-pill-accompagnement"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/5 border border-black/10 text-[#0A0A0A]/80 font-light hover:border-[#E52431]/40 hover:text-[#0A0A0A] transition-all"
            >
              Accompagnement complet
            </a>
            <a
              href="/#tarifs"
              data-testid="voir-aussi-pill-tarifs"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/5 border border-black/10 text-[#0A0A0A]/80 font-light hover:border-[#E52431]/40 hover:text-[#0A0A0A] transition-all"
            >
              Tarifs
            </a>
            <a
              href="/#audit"
              data-testid="voir-aussi-pill-audit"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/5 border border-black/10 text-[#0A0A0A]/80 font-light hover:border-[#E52431]/40 hover:text-[#0A0A0A] transition-all"
            >
              Audit 190 € TTC
            </a>
            <a
              href="/#faq"
              data-testid="voir-aussi-pill-faq"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/5 border border-black/10 text-[#0A0A0A]/80 font-light hover:border-[#E52431]/40 hover:text-[#0A0A0A] transition-all"
            >
              FAQ générale
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="relative py-24 md:py-32 overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #E52431 0%, #7E1945 35%, #2A2A5F 70%, #1F2C6E 100%)",
        }}
      >
        <div className="absolute inset-0 bg-[#0A0A0A]/20" />
        <div className="grain absolute inset-0" />

        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-outfit text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-white leading-[1.05]">
            Votre{" "}
            <span className="font-medium italic">nouveau logo</span> commence
            ici.
          </h2>
          <p className="mt-6 text-white/80 text-lg font-light max-w-2xl mx-auto">
            Parlez-nous de votre projet à La Réunion. Nous revenons vers vous
            sous 24h avec un plan personnalisé et un devis précis.
          </p>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <button
              onClick={openQuote}
              className="inline-flex items-center gap-2 rounded-full bg-white text-[#0A0A0A] font-semibold px-10 py-5 text-sm uppercase tracking-[0.15em] hover:scale-[1.03] hover:shadow-[0_0_50px_rgba(255,255,255,0.3)] transition-all duration-300 btn-shine"
            >
              Créer mon logo dès maintenant
              <ArrowRight size={16} />
            </button>
            <a
              href="tel:+262262667970"
              className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/30 text-white font-semibold px-10 py-5 text-sm uppercase tracking-[0.15em] hover:bg-white/20 transition-all duration-300"
            >
              +262 262 66 79 70
            </a>
          </motion.div>
          <p className="mt-6 text-xs uppercase tracking-[0.2em] text-white/70">
            Maurice · La Réunion · France · Réponse sous 24h
          </p>
        </div>
      </section>

      {/* Mini footer */}
      <footer className="py-10 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/50">
          <div>
            © {new Date().getFullYear()} EM Group — Agence digitale premium.
          </div>
          <div className="flex gap-5">
            <Link to="/mentions-legales" className="hover:text-white/80">
              Mentions légales
            </Link>
            <Link to="/confidentialite" className="hover:text-white/80">
              Confidentialité
            </Link>
            <Link to="/cgv" className="hover:text-white/80">
              CGV
            </Link>
          </div>
        </div>
      </footer>

      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
    </div>
  );
}
